import { useState, useEffect } from 'react';
import SelectOption from './SelectOption';
import { datadogLogs } from '@datadog/browser-logs';
import { Alert, Box, Typography } from '@coolblue-development/freddy';
import SettingsSection from './SettingsSection';
import VoiceEnhancementToggle from './VoiceEnhancementToggle';
import EmailViewTimerToggle from './EmailViewTimerToggle';

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

const DEVICE_LOAD_ATTEMPTS = 3;
const DEVICE_LOAD_TIMEOUT_MS = 3000;

const Settings = (): JSX.Element => {
  const [audioInputDevices, setAudioInputDevices] = useState<AudioDevice[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<AudioDevice[]>([]);
  const [devicesFailed, setDevicesFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadDevices = async (): Promise<void> => {
      for (let attempt = 1; attempt <= DEVICE_LOAD_ATTEMPTS && !cancelled; attempt++) {
        try {
          const devices = (await connect.core.getFrameMediaDevices(DEVICE_LOAD_TIMEOUT_MS)) as AudioDevice[];
          if (cancelled) return;
          setAudioInputDevices(devices.filter(device => device.kind === 'audioinput'));
          setAudioOutputDevices(devices.filter(device => device.kind === 'audiooutput'));
          setDevicesFailed(false);
          return;
        } catch (err) {
          if (cancelled) return;
          datadogLogs.logger.error(
            `Unable to get media devices (attempt ${attempt}/${DEVICE_LOAD_ATTEMPTS})`,
            { mediaDevices: true },
            err as Error
          );
        }
      }
      if (!cancelled) setDevicesFailed(true);
    };

    loadDevices();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        boxShadow: '0px 4px 16px 0px #246CB429',
        borderRadius: '12px',
        overflowY: 'auto'
      }}
    >
      <Box
        sx={{
          height: '60px',
          background: '#fff',
          borderBottom: '#DDDDDD 1px solid',
          display: 'flex',
          alignItems: 'center',
          padding: '15px',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Typography variant="h4">Settings</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0 16px', padding: '24px 15px' }}>
        <Box sx={{ flex: '1 1 360px', maxWidth: '560px', minWidth: 0 }}>
          <SettingsSection title="Audio devices" description="Where you hear calls and where your voice is picked up.">
            {devicesFailed && (
              <Box sx={{ marginBottom: '16px' }}>
                <Alert
                  intent="warning"
                  description="Your audio devices could not be loaded. Reload the page and allow microphone access if asked."
                />
              </Box>
            )}
            <SelectOption name="Speaker" devices={audioOutputDevices} label="Speaker" />
            <SelectOption name="Microphone" devices={audioInputDevices} label="Microphone" />
            <SelectOption name="Ringer" devices={audioOutputDevices} label="Ringer" />
          </SettingsSection>
          <SettingsSection title="Call quality" description="Improve how you sound to customers.">
            <VoiceEnhancementToggle />
          </SettingsSection>
        </Box>
        <Box sx={{ flex: '1 1 320px', maxWidth: '560px', minWidth: 0 }}>
          <SettingsSection title="Email" description="Choose what you see while handling emails.">
            <EmailViewTimerToggle />
          </SettingsSection>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
